import { useOperations } from '@api/hooks/useOperations';
import { EmptyState } from '@components/EmptyState';
import { PageContainer } from '@components/PageContainer';
import { PermissionGate } from '@components/PermissionGate';
import {
	Button,
	DropdownPositions,
	FileAddIcon,
	FileAttachment,
	OperationIcon,
	Pagination,
	SignIcon,
	Table,
} from '@gurupay/gp-ui-library';
import { usePagination } from '@hooks/usePagination';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OperationStatus } from 'types/operations';
import { Capability } from 'types/roles';

import {
	type OperationsFilterParams,
	OperationsFiltersModal,
} from './components/OperationsFiltersModal';
import { useOperationsTable } from './hooks/useOperationsTable';

const OperationsPage: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { columns, renders, showHeader } = useOperationsTable();
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();

	const [searchParams] = useSearchParams();
	const [initialFilters] = useState<OperationsFilterParams>(() => {
		const status = searchParams.getAll('status');
		return (
			status.length
				? {
						status: status[0]
							.toLowerCase()
							.split(',')
							.map((s) => s.trim())
							.filter((s): s is OperationStatus =>
								Object.values(OperationStatus).includes(s as OperationStatus),
							),
					}
				: {}
		) as OperationsFilterParams;
	});

	const [filters, setFilters] = useState<OperationsFilterParams>(initialFilters);
	const filterForm = useForm<OperationsFilterParams>({ defaultValues: {} });

	useEffect(() => {
		if (initialFilters?.status?.length) {
			filterForm.reset(initialFilters, { keepDefaultValues: true });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { data, isLoading } = useOperations({
		page: currentPage,
		limit: pageSize,
		...filters,
	});

	const handleFiltersSubmit = (params: OperationsFilterParams) => {
		setFilters(params);
		setCurrentPage(1);
	};

	return (
		<PageContainer icon={<OperationIcon />} title={t('newOperations.title')}>
			<div className="flex flex-wrap items-center gap-space-8">
				<Button
					href="/statements"
					className="flex-row-reverse"
					icon={<FileAddIcon className="w-space-16" />}
					variant="primary-black"
				>
					{t('statements.statements')}
				</Button>

				<PermissionGate capability={Capability.SIGN_PAYMENT}>
					<Button
						href="/operations/sign"
						className="flex-row-reverse"
						icon={<SignIcon className="w-space-16" />}
						variant="secondary"
					>
						{t('newOperations.sign')}
					</Button>
				</PermissionGate>

				<Button
					href="/operations/batch"
					className="flex-row-reverse"
					icon={<FileAttachment className="w-space-16" />}
					variant="secondary"
				>
					{t('newOperations.batchFiles')}
				</Button>

				<FormProvider {...filterForm}>
					<OperationsFiltersModal
						hasActiveFilters={!!filters.status?.length}
						onFiltersSubmit={handleFiltersSubmit}
					/>
				</FormProvider>
			</div>

			{data?.items?.length || isLoading ? (
				<Table
					className="mt-space-40 max-mobile:mt-space-24"
					columns={columns}
					getRowKey={(row) => row.id}
					isLoading={isLoading}
					onRowClick={({ id }) => navigate(`/payments/${id}`)}
					renders={renders}
					rows={data?.items ?? []}
					showHeader={showHeader}
				/>
			) : (
				<PermissionGate
					capability={Capability.CREATE_PAYMENT}
					fallback={
						<EmptyState
							icon={<OperationIcon />}
							subTitle={t('listOfOperations.empty.readOnly.subTitle')}
							title={t('listOfOperations.empty.readOnly.title')}
						/>
					}
				>
					<EmptyState
						buttonProps={{
							children: t('listOfOperations.empty.buttonText'),
							href: '/payments',
						}}
						icon={<OperationIcon />}
						subTitle={t('listOfOperations.empty.pending.subTitle')}
						title={t('listOfOperations.empty.pending.title')}
					/>
				</PermissionGate>
			)}

			{data?.pagination && data.pagination.count > pageSize && (
				<Pagination
					className="mt-space-24!"
					currentPage={currentPage}
					dropdownOpenPosition={DropdownPositions.TOP}
					onPageChange={setCurrentPage}
					onPageSizeChange={handlePageSizeChange}
					pageOptions={pageOptions}
					pageSize={pageSize}
					pageText={pageText}
					totalCount={data.pagination.count}
					totalCountText={totalCountText}
				/>
			)}
		</PageContainer>
	);
};

export default OperationsPage;
